import apiClient from './axios.config';
import { API_ENDPOINTS } from '../../constants';

/**
 * Assets API service
 */
export const assetsAPI = {
  /**
   * Get all assets with filtering and pagination
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  getAll: (params = {}) => {
    return apiClient.get(API_ENDPOINTS.ASSETS, { params });
  },

  /**
   * Get asset by ID
   * @param {number} assetId - Asset ID
   * @returns {Promise} API response
   */
  getById: (assetId) => {
    return apiClient.get(API_ENDPOINTS.ASSET_BY_ID(assetId));
  },

  /**
   * Create new asset
   * @param {Object} assetData - Asset data
   * @returns {Promise} API response
   */
  create: (assetData) => {
    return apiClient.post(API_ENDPOINTS.ASSETS, assetData);
  },

  /**
   * Update asset
   * @param {number} assetId - Asset ID
   * @param {Object} assetData - Updated asset data
   * @returns {Promise} API response
   */
  update: (assetId, assetData) => {
    return apiClient.put(API_ENDPOINTS.ASSET_BY_ID(assetId), assetData);
  },

  /**
   * Delete asset
   * @param {number} assetId - Asset ID
   * @returns {Promise} API response
   */
  delete: (assetId) => {
    return apiClient.delete(API_ENDPOINTS.ASSET_BY_ID(assetId));
  },

  /**
   * Dispose asset
   * @param {number} assetId - Asset ID
   * @param {Object} disposalData - Disposal data
   * @returns {Promise} API response
   */
  dispose: (assetId, disposalData) => {
    return apiClient.post(API_ENDPOINTS.ASSET_DISPOSE(assetId), disposalData);
  },

  /**
   * Get asset statistics
   * @returns {Promise} API response
   */
  getStatistics: () => {
    return apiClient.get(API_ENDPOINTS.ASSET_STATISTICS);
  },

  /**
   * Generate asset code
   * @param {string} category - Asset category
   * @returns {string} Generated asset code
   */
  generateAssetCode: (category) => {
    const prefix = category.replace(/\s+/g, '').toUpperCase();
    const timestamp = Date.now().toString().slice(-6);
    return `${prefix}-${timestamp}`;
  },

  // ── Maintenance ────────────────────────────────────────────────

  getAllMaintenance: (params = {}) =>
    apiClient.get(API_ENDPOINTS.ASSET_MAINTENANCE, { params }),

  getMaintenanceById: (id) =>
    apiClient.get(API_ENDPOINTS.ASSET_MAINTENANCE_BY_ID(id)),

  createMaintenance: (data) =>
    apiClient.post(API_ENDPOINTS.ASSET_MAINTENANCE, data),

  updateMaintenance: (id, data) =>
    apiClient.put(API_ENDPOINTS.ASSET_MAINTENANCE_BY_ID(id), data),

  updateMaintenanceStatus: (id, status, completed_date) =>
    apiClient.patch(API_ENDPOINTS.ASSET_MAINTENANCE_STATUS(id), { status, completed_date }),

  deleteMaintenance: (id) =>
    apiClient.delete(API_ENDPOINTS.ASSET_MAINTENANCE_BY_ID(id)),

  /**
   * Export assets to CSV
   * @param {Array} assets - Assets data
   * @param {string} filename - Export filename
   */
  exportToCSV: (assets, filename = 'assets-export.csv') => {
    const headers = [
      'Asset Code',
      'Asset Name',
      'Category',
      'Description',
      'Purchase Date',
      'Purchase Cost',
      'Current Value',
      'Depreciation Rate',
      'Useful Life',
      'Location',
      'Custodian',
      'Serial Number',
      'Supplier',
      'Warranty Expiry',
      'Condition',
      'Status',
      'Last Maintenance',
      'Next Maintenance',
      'Barcode',
      'Notes',
      'Department',
      'Created By',
      'Created At'
    ];

    const csvContent = [
      headers.join(','),
      ...assets.map(asset => [
        asset.asset_code,
        `"${asset.asset_name}"`,
        asset.category,
        `"${asset.description || ''}"`,
        asset.purchase_date,
        asset.purchase_cost,
        asset.current_value,
        asset.depreciation_rate,
        asset.useful_life,
        `"${asset.location}"`,
        `"${asset.custodian}"`,
        asset.serial_number || '',
        `"${asset.supplier || ''}"`,
        asset.warranty_expiry || '',
        asset.condition,
        asset.status,
        asset.last_maintenance || '',
        asset.next_maintenance || '',
        asset.barcode || '',
        `"${asset.notes || ''}"`,
        `"${asset.department?.department_name || ''}"`,
        `"${asset.creator?.first_name} ${asset.creator?.sur_name}"`,
        asset.created_at
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export default assetsAPI;